/**
 * Claim an old EduFocal account from an emailed link.
 *
 * The other way in, signing in with the password from the old site, only works for
 * someone who still remembers a password they last typed years ago. This page is for
 * everyone else: the link proved they reach the mailbox on the legacy record, and the
 * Academy account is created here, when they choose a password.
 */
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useLocation, Link } from "wouter";
import { AlertCircle, Loader2, ArrowLeft } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface TokenState {
  valid: boolean;
  firstName?: string | null;
  email?: string;
  school?: string | null;
  error?: string;
}

export default function ClaimAccount() {
  const [, navigate] = useLocation();
  const [token, setToken] = useState("");
  const [state, setState] = useState<TokenState | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const tokenParam = new URLSearchParams(window.location.search).get("token");
    if (!tokenParam) {
      setState({ valid: false, error: "This link is missing its code. Open it from the email again." });
      return;
    }
    setToken(tokenParam);
    fetch(`/api/auth/verify-claim-token/${encodeURIComponent(tokenParam)}`)
      .then(res => res.json())
      .then(setState)
      .catch(() => setState({ valid: false, error: "We couldn't check this link. Please try again." }));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast({ title: "Passwords don't match", variant: "destructive" });
      return;
    }
    if (password.length < 8) {
      toast({ title: "Password must be at least 8 characters", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await apiRequest("POST", "/api/auth/claim-account", { token, newPassword: password });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to claim this account");

      // The endpoint signs them in, so the app only has to catch up.
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: "Welcome back!", description: "Your old account is yours again." });
      navigate("/dashboard");
    } catch (error: any) {
      toast({ title: "Couldn't claim the account", description: error.message, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!state) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!state.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>This link doesn't work</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription data-testid="text-claim-error">{state.error}</AlertDescription>
            </Alert>
            <p className="text-sm text-muted-foreground">
              Claim links expire after an hour. Request a new one and we'll send it straight away.
            </p>
            <Link href="/forgot-password">
              <Button className="w-full" data-testid="button-new-link">Send me a new link</Button>
            </Link>
            <Link href="/login">
              <Button variant="ghost" className="w-full" data-testid="link-back-login">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to sign in
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle data-testid="text-claim-title">
            {state.firstName ? `Welcome back, ${state.firstName}` : "Welcome back"}
          </CardTitle>
          <CardDescription>
            We found your old EduFocal account on <strong>{state.email}</strong>
            {state.school ? ` (${state.school})` : ""}. Choose a password and everything you
            had already worked through comes with you.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New password</Label>
              <Input
                id="password"
                type="password"
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
                autoComplete="new-password"
                required
                autoFocus
                data-testid="input-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm password</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder="Repeat your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isSubmitting}
                autoComplete="new-password"
                required
                data-testid="input-confirm-password"
              />
            </div>
            <Button type="submit" className="w-full" disabled={isSubmitting} data-testid="button-claim">
              {isSubmitting ? "Setting up your account..." : "Set password and sign in"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
